import { useEffect, useRef, useState } from "react";

import { extractTextFromNdefMessage, type WebNdefMessageLike } from "./nfc-scan";

// Web NFC tap listener (Android Chrome only — callers gate on device.hasWebNfc). Browser glue,
// fail-safe by design: missing API / denied permission / scan failure surfaces a guard-facing
// message via onError, never a crash; the scan is aborted on unmount/cancel. Fires onTag with the
// decoded NDEF text (or null when the tag carried no readable text record) — the caller validates
// against the shared checkpoint-token contract and shows the safe steer-to-QR copy on junk tags.

interface NdefReaderLike {
  scan(options?: { signal?: AbortSignal }): Promise<void>;
  onreading: ((event: { message?: WebNdefMessageLike }) => void) | null;
  onreadingerror: (() => void) | null;
}

type NdefReaderCtor = new () => NdefReaderLike;

export function NfcScanner({
  onTag,
  onClose,
  onError,
}: {
  onTag: (text: string | null) => void;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const onTagRef = useRef(onTag);
  const onErrorRef = useRef(onError);
  onTagRef.current = onTag;
  onErrorRef.current = onError;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const Reader = (window as unknown as { NDEFReader?: NdefReaderCtor }).NDEFReader;
    if (!Reader) {
      onErrorRef.current("NFC isn't available on this device — use QR instead.");
      return;
    }
    const controller = new AbortController();
    let stopped = false;
    try {
      const reader = new Reader();
      reader.onreading = (event) => {
        if (stopped) {
          return;
        }
        stopped = true;
        controller.abort();
        onTagRef.current(extractTextFromNdefMessage(event.message));
      };
      reader.onreadingerror = () => {
        // Unreadable/garbled tag — keep listening; the hint already says to hold steady.
      };
      reader
        .scan({ signal: controller.signal })
        .then(() => {
          if (!stopped) {
            setReady(true);
          }
        })
        .catch(() => {
          if (!stopped) {
            stopped = true;
            onErrorRef.current(
              "Couldn't start NFC. Check the NFC permission, or use QR/manual instead.",
            );
          }
        });
    } catch {
      onErrorRef.current("Couldn't start NFC. Use QR or the manual fallback instead.");
    }
    return () => {
      stopped = true;
      controller.abort();
    };
  }, []);

  return (
    <div className="nfc-scanner" role="status" aria-label="NFC tap listener">
      <div className="nfc-scanner__wave" aria-hidden="true" />
      <p className="nfc-scanner__hint">
        {ready
          ? "Hold the checkpoint tag against the back of your phone."
          : "Starting NFC…"}
      </p>
      <button className="nfc-scanner__cancel" type="button" onClick={onClose}>
        Cancel
      </button>
    </div>
  );
}
